import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, computed, signal } from '@angular/core';
import { ClarityModule, ClrDatagridSortOrder } from '@clr/angular';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LOCALE_ID } from '@angular/core';
import { TranscriptionEditorComponent } from '../../transcription/components/transcription-editor/transcription-editor.component';
import { OverlayPanelComponent } from '../../../shared/components/overlay-panel/overlay-panel.component';
import { Broadcast } from '../../../models/broadcast';
import { BroadcastStatisticsService } from '../broadcast-statistics.service';
import { BroadcastStatistics, BroadcastSummary, StatisticsRange } from '../../../models/broadcast-statistic';
import { downloadCsv } from '../../../shared/utils/csv-export';

/**
 * Whisper-Tab der Statistikseite — die bisherige Seite, unverändert in
 * Zahlen und Spalten, nur ohne eigene Zeitraum-/Senderauswahl: die kommt
 * jetzt als StatisticsRange von der Shell.
 */
@Component({
  selector: 'app-whisper-statistics',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule, TranscriptionEditorComponent, OverlayPanelComponent],
  providers: [{ provide: LOCALE_ID, useValue: 'de-DE' }],
  templateUrl: './whisper-statistics.component.html',
  styleUrl: './whisper-statistics.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WhisperStatisticsComponent implements OnChanges {
  /** Zeitraum + Sender; neue Objektidentität = neu laden. */
  @Input() range: StatisticsRange | null = null;
  /** Nur der sichtbare Tab lädt — sonst zahlt jeder Tabwechsel doppelt. */
  @Input() active = false;

  private readonly broadcastSummaries = signal<BroadcastSummary[]>([]);
  readonly broadcastList = computed(() => this.broadcastSummaries());

  statistics: BroadcastStatistics = {} as BroadcastStatistics;

  sortOrder = ClrDatagridSortOrder.ASC;
  editorVisible = false;
  selectedBroadcast: any = null;
  isLoading = false;

  /** Zuletzt geladener Range — verhindert erneutes Laden beim Tabwechsel. */
  private loadedRange: StatisticsRange | null = null;

  constructor(private statsService: BroadcastStatisticsService) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.loadIfNeeded();
  }

  private loadIfNeeded(): void {
    if (!this.active || !this.range || this.range === this.loadedRange) {
      return;
    }

    const range = this.range;
    this.loadedRange = range;

    if (!range.channelFilter) {
      this.statistics = {} as BroadcastStatistics;
      this.broadcastSummaries.set([]);
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.statsService.getBroadcastStatistics(range.fromDate, range.toDateExclusive, range.channelFilter)
      .subscribe({
        next: (data) => {
          this.broadcastSummaries.set(data?.broadcasts ?? []);
          this.statistics = data ?? ({} as BroadcastStatistics);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Fehler beim Laden der Broadcast-Statistiken', err);
          // Range freigeben, damit ein Tabwechsel es erneut versucht.
          this.loadedRange = null;
          this.isLoading = false;
        }
      });
  }

  editBroadcast(broadcast: Broadcast): void {
    this.selectedBroadcast = broadcast;
    this.editorVisible = true;
  }

  closeEditor() {
    this.editorVisible = false;
    this.selectedBroadcast = null;
  }

  exportToCsv() {
    if (!this.range) {
      return;
    }

    const headers = [
      'Key',
      'Programmdatum',
      'Channel',
      'Title',
      'Subtitle',
      'BruttoDuration',
      'Kiut',
      'isPretranscribed',
      'PercentageTranscribed',
      'WavKey'
    ];

    const rows = this.broadcastList().map(broadcast => [
      broadcast.key,
      broadcast.programmDate,
      broadcast.channel ?? '',
      broadcast.title ?? '',
      broadcast.subtitle ?? '',
      broadcast.bruttoDuration ?? '',
      broadcast.kiut ?? '',
      broadcast.isPretranscribed ?? '',
      broadcast.percentageTranscribed ?? '',
      broadcast.wavKey ?? ''
    ]);

    const from = this.range.fromDate.toISOString().split('T')[0];
    const to = this.range.toDate.toISOString().split('T')[0];
    downloadCsv(`Statistiken_${from}_${to}.csv`, headers, rows);
  }
}
